import { useState, type ReactNode } from 'react';
import { AlertTriangle, Check, Eye, Undo2 } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, Chargement, MessageErreur, Pastille, PastilleSite } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { jourCivilReunion } from '../../lib/dates-civiles';
import { VisionneusePiece } from '../dossier/VisionneusePiece';
import { ModaleMotif, ModalePaiement } from './GestesFactureFormateur';
import { ControlesFacture, libelleAlertes } from './ControlesFacture';
import {
  useAnnulerPaiementFormateur,
  useDossierFacture,
  usePayerFacture,
  useRefuserFactureFormateur,
  useRenvoyerFactureFormateur,
  useValiderFactureFormateur,
} from '../../lib/useCircuitFactures';
import { dateReunion, euros, heures, jourCourt, messageRefus, urlFactureFormateur } from '../../lib/useFormateurs';

type Geste = 'RENVOI' | 'REFUS' | 'ANNULATION' | 'PAIEMENT';

function Ligne({ libelle, children }: { libelle: string; children: ReactNode }): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:justify-between sm:gap-3">
      <dt className="shrink-0 text-xs text-codialis-gray sm:text-sm">{libelle}</dt>
      <dd className="min-w-0 break-words text-sm font-medium text-codialis-navy sm:text-right">{children}</dd>
    </div>
  );
}

/**
 * Contrôle d'une facture formateur (TF-401) : ce qui sera payé — SIRET, IBAN
 * recopiés à la transmission, fiches séquence validées, heures et montant au
 * regard du repère heures × taux, et leurs contrôles (TF-402) face à la fiche
 * du formateur et à ses demi-journées — puis le geste que l'étape permet : valider
 * ou renvoyer (pôle facturation), payer, refuser ou annuler un paiement
 * (Direction). Chaque refus vient de l'API ; la lecture du dossier est tracée.
 */
export function DossierFactureFormateur({
  factureId,
  onFermer,
}: {
  factureId: string;
  onFermer: () => void;
}): JSX.Element {
  const toast = useToast();
  const { data, isLoading, error } = useDossierFacture(factureId);
  const valider = useValiderFactureFormateur();
  const renvoyer = useRenvoyerFactureFormateur();
  const refuser = useRefuserFactureFormateur();
  const annuler = useAnnulerPaiementFormateur();
  const payer = usePayerFacture();
  const [geste, setGeste] = useState<Geste | null>(null);
  const [apercu, setApercu] = useState(false);
  const facture = data?.facture;

  const titre = facture ? `Contrôle · Facture n° ${facture.numero}` : 'Contrôle de la facture';
  const ecart =
    facture && facture.montantAttendu !== null
      ? Math.round((facture.montant - facture.montantAttendu) * 100) / 100
      : null;

  return (
    <Modale
      titre={titre}
      sousTitre={facture ? `${facture.formateur.nom} · antenne ${facture.siteCode}` : undefined}
      ouverte
      onFermer={onFermer}
    >
      <div className="space-y-5 p-4 sm:p-6">
        {isLoading && <Chargement libelle="Chargement de la facture…" />}
        {error && <MessageErreur message={messageRefus(error, 'La facture n’a pas pu être chargée.')} />}

        {facture && (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Pastille texte={facture.etatLibelle} ton={facture.etatTon} />
              {facture.typeLibelle && (
                <Pastille texte={facture.typeLibelle} ton={facture.type === 'CFA' ? 'info' : 'mint'} />
              )}
              <PastilleSite site={facture.siteCode} />
            </div>

            <dl className="space-y-2.5">
              <Ligne libelle="Formateur">{facture.formateur.nom}</Ligne>
              <Ligne libelle="Période">
                Du {jourCourt(facture.debut)} au {jourCourt(facture.fin)}
              </Ligne>
              <Ligne libelle="SIRET">
                <span className="font-mono">{facture.siret}</span>
              </Ligne>
              <Ligne libelle="IBAN">
                <span className="break-all font-mono">{facture.iban ?? 'Illisible'}</span>
              </Ligne>
              {(facture.bic || facture.titulaireCompte) && (
                <Ligne libelle="BIC · titulaire">
                  {[facture.bic, facture.titulaireCompte].filter(Boolean).join(' · ')}
                </Ligne>
              )}
              <Ligne libelle="Séquences validées">{facture.nombreFiches}</Ligne>
              <Ligne libelle="Heures facturées">{heures(facture.heuresValidees)}</Ligne>
              <Ligne libelle="Taux horaire">
                {facture.tauxHoraire !== null ? `${euros(facture.tauxHoraire)} HT` : 'Non fixé'}
              </Ligne>
              <Ligne libelle="Repère heures × taux">
                {facture.montantAttendu !== null ? `${euros(facture.montantAttendu)} HT` : '—'}
              </Ligne>
              <Ligne libelle="Montant">
                <span className="font-display text-base font-extrabold">{euros(facture.montant)}</span>
                {ecart !== null && ecart !== 0 && (
                  <span className="ml-1 text-xs font-semibold text-amber-700">
                    (écart {ecart > 0 ? '+' : ''}
                    {euros(ecart)})
                  </span>
                )}
              </Ligne>
              {facture.payeeLe && (
                <Ligne libelle="Payée le">
                  {jourCourt(facture.payeeLe)}
                  {facture.referencePaiement ? ` · réf. ${facture.referencePaiement}` : ''}
                </Ligne>
              )}
            </dl>

            {facture.controles.length > 0 && (
              <section aria-labelledby={`controles-${facture.id}`}>
                <h3
                  id={`controles-${facture.id}`}
                  className="mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                >
                  Contrôles · {facture.alertes > 0 ? libelleAlertes(facture.alertes) : 'aucun point à contrôler'}
                </h3>
                <ControlesFacture controles={facture.controles} />
              </section>
            )}
            {facture.alertes === 0 && facture.nombreFiches > 0 && (
              <p className="rounded-xl bg-codialis-mint/10 p-3 text-xs text-codialis-navy">
                Cohérence vérifiée : heures facturées = heures des fiches séquence validées par le responsable
                pédagogique.
              </p>
            )}
            {facture.piecesNonConformes.length > 0 && (
              <p className="flex gap-2 rounded-xl bg-red-50 p-3 text-xs text-red-800">
                <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
                Pièces du formateur non conformes aujourd’hui : {facture.piecesNonConformes.join(', ')}.
              </p>
            )}

            <div>
              <BoutonSecondaire onClick={() => setApercu(true)}>
                <Eye className="mr-1.5 h-4 w-4" aria-hidden="true" />
                Voir la facture
              </BoutonSecondaire>
            </div>

            {facture.fiches.length > 0 && (
              <section aria-labelledby={`fiches-${facture.id}`}>
                <h3
                  id={`fiches-${facture.id}`}
                  className="mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                >
                  Fiches séquence rattachées
                </h3>
                <ul className="divide-y divide-gray-100 rounded-xl border border-gray-100 text-xs">
                  {facture.fiches.map((fiche) => (
                    <li key={fiche.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                      <span className="min-w-0 text-codialis-navy">
                        {jourCourt(fiche.date)} · {fiche.titre}
                      </span>
                      <span className="text-codialis-gray">
                        {fiche.typeLibelle} · {heures(fiche.heures)}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {facture.historique.length > 0 && (
              <section aria-labelledby={`historique-${facture.id}`}>
                <h3
                  id={`historique-${facture.id}`}
                  className="mb-2 text-[11px] font-bold uppercase tracking-wider text-codialis-gray"
                >
                  Historique
                </h3>
                <ol className="space-y-1.5 text-xs">
                  {facture.historique.map((ligne) => (
                    <li key={ligne.id} className="text-codialis-gray">
                      <span className="font-semibold text-codialis-navy">{ligne.libelle}</span> · le{' '}
                      {dateReunion(ligne.le)}
                      {ligne.auteur ? ` par ${ligne.auteur}` : ''}
                      {ligne.payeeLe ? ` · virement du ${jourCourt(ligne.payeeLe)}` : ''}
                      {ligne.referencePaiement ? ` (réf. ${ligne.referencePaiement})` : ''}
                      {ligne.motif ? ` · ${ligne.motif}` : ''}
                    </li>
                  ))}
                </ol>
              </section>
            )}

            <Gestes
              statut={facture.statut}
              refus={{
                validation: facture.refusValidation,
                renvoi: facture.refusRenvoi,
                paiement: facture.refusPaiement,
                refus: facture.refusRefus,
                annulation: facture.refusAnnulationPaiement,
              }}
              validation={{
                enCours: valider.isPending,
                erreur: valider.error,
                lancer: () =>
                  valider.mutate(factureId, {
                    onSuccess: () => {
                      toast('Facture validée · elle part au prochain lot pour la Direction');
                      onFermer();
                    },
                  }),
              }}
              onGeste={setGeste}
            />
          </>
        )}
      </div>

      {facture && apercu && (
        <VisionneusePiece
          ouverte
          libellePiece={`Facture n° ${facture.numero} — ${facture.formateur.nom}`}
          fichiers={[
            {
              id: 'facture',
              nomFichier: facture.nomFichier,
              typeMime: facture.typeMime,
              tailleOctets: 0,
              deposeLe: '',
            },
          ]}
          urlFichier={() => urlFactureFormateur(facture.id)}
          onFermer={() => setApercu(false)}
        />
      )}

      {facture && geste === 'RENVOI' && (
        <ModaleMotif
          titre={`Renvoyer la facture n° ${facture.numero}`}
          sousTitre={`${facture.formateur.nom} · le motif lui est adressé dans son espace et par e-mail`}
          libelle="Motif du renvoi"
          aide="La facture revient au formateur, « à corriger ». Corrigée, elle repasse par la vérification de l’assistant(e), puis par vous."
          bouton="Renvoyer au formateur"
          enCours={renvoyer.isPending}
          erreur={renvoyer.error}
          onConfirmer={(motif) =>
            renvoyer.mutate(
              { factureId, motif },
              {
                onSuccess: () => {
                  toast('Facture renvoyée au formateur');
                  setGeste(null);
                  onFermer();
                },
              },
            )
          }
          onFermer={() => setGeste(null)}
        />
      )}
      {facture && geste === 'REFUS' && (
        <ModaleMotif
          titre={`Refuser la facture n° ${facture.numero}`}
          sousTitre={`${facture.formateur.nom} · le formateur et le pôle facturation sont prévenus`}
          libelle="Motif du refus"
          aide="La facture sort du lot et revient au formateur, « à corriger ». Corrigée, elle refait tout le circuit."
          bouton="Refuser la facture"
          enCours={refuser.isPending}
          erreur={refuser.error}
          onConfirmer={(motif) =>
            refuser.mutate(
              { factureId, motif },
              {
                onSuccess: () => {
                  toast('Facture refusée · renvoyée au formateur');
                  setGeste(null);
                  onFermer();
                },
              },
            )
          }
          onFermer={() => setGeste(null)}
        />
      )}
      {facture && geste === 'ANNULATION' && (
        <ModaleMotif
          titre={`Annuler le paiement de la facture n° ${facture.numero}`}
          sousTitre="Pour un paiement noté par erreur : le formateur est prévenu"
          libelle="Motif de l’annulation"
          aide="La facture revient « transmise à la Direction », dans son lot. L’historique garde le paiement annulé."
          bouton="Annuler le paiement"
          enCours={annuler.isPending}
          erreur={annuler.error}
          onConfirmer={(motif) =>
            annuler.mutate(
              { factureId, motif },
              {
                onSuccess: () => {
                  toast('Paiement annulé');
                  setGeste(null);
                },
              },
            )
          }
          onFermer={() => setGeste(null)}
        />
      )}
      {facture && geste === 'PAIEMENT' && (
        <ModalePaiement
          titre={`Paiement de la facture n° ${facture.numero}`}
          resume={`${facture.formateur.nom} · ${euros(facture.montant)}`}
          transmisLe={facture.lot ? jourCivilReunion(facture.lot.transmisLe) : null}
          enCours={payer.isPending}
          erreur={payer.error}
          onConfirmer={(saisie) =>
            payer.mutate(
              { factureId, saisie },
              {
                onSuccess: () => {
                  toast('Paiement noté · le formateur est prévenu');
                  setGeste(null);
                },
              },
            )
          }
          onFermer={() => setGeste(null)}
        />
      )}
    </Modale>
  );
}

/**
 * Les gestes que l'étape de la facture appelle. Un geste de l'étape refusé par
 * l'API (pièces non conformes, aucune fiche…) reste visible, désactivé, avec
 * son motif ; ceux d'une autre étape ne s'affichent pas.
 */
function Gestes({
  statut,
  refus,
  validation,
  onGeste,
}: {
  statut: string;
  refus: {
    validation: string | null;
    renvoi: string | null;
    paiement: string | null;
    refus: string | null;
    annulation: string | null;
  };
  validation: { enCours: boolean; erreur: unknown; lancer: () => void };
  onGeste: (geste: Geste) => void;
}): JSX.Element | null {
  const etapeFacturation = statut === 'VERIFIEE' || statut === 'VALIDEE';
  const boutons: ReactNode[] = [];

  if (statut === 'VERIFIEE') {
    boutons.push(
      <BoutonPrincipal
        key="valider"
        onClick={validation.lancer}
        disabled={refus.validation !== null || validation.enCours}
      >
        <Check className="mr-1.5 h-4 w-4" aria-hidden="true" />
        {validation.enCours ? 'Validation…' : 'Valider'}
      </BoutonPrincipal>,
    );
  }
  if (etapeFacturation && refus.renvoi === null) {
    boutons.push(
      <BoutonSecondaire key="renvoyer" onClick={() => onGeste('RENVOI')}>
        <Undo2 className="mr-1.5 h-4 w-4" aria-hidden="true" />
        Renvoyer au formateur
      </BoutonSecondaire>,
    );
  }
  if (refus.paiement === null) {
    boutons.push(
      <BoutonPrincipal key="payer" onClick={() => onGeste('PAIEMENT')}>
        Noter le paiement
      </BoutonPrincipal>,
    );
  }
  if (refus.refus === null) {
    boutons.push(
      <BoutonSecondaire key="refuser" onClick={() => onGeste('REFUS')}>
        Refuser
      </BoutonSecondaire>,
    );
  }
  if (refus.annulation === null) {
    boutons.push(
      <BoutonSecondaire key="annuler" onClick={() => onGeste('ANNULATION')}>
        Annuler le paiement
      </BoutonSecondaire>,
    );
  }
  if (boutons.length === 0) return null;

  return (
    <div className="space-y-2 border-t border-gray-100 pt-4">
      {statut === 'VERIFIEE' && refus.validation !== null && (
        <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">{refus.validation}</p>
      )}
      {validation.erreur !== null && validation.erreur !== undefined && (
        <p role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">
          {messageRefus(validation.erreur, 'La facture n’a pas pu être validée.')}
        </p>
      )}
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">{boutons}</div>
    </div>
  );
}
